import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { MassUploadGridService } from '../../../producer/mass-upload-grid/services/mass-upload-grid.service';
import { County } from '@ptl/global-components';
import { DropdownOption, MassUploadEntryConfig } from '../../../producer/mass-upload-grid/interfaces/mass-upload-grid.interface';
import { MassUploadGridDataHelperService } from '../../../producer/mass-upload-grid/services/mug-data-helper.service';
import { EntitlementsHelperService } from '@ptl/account-management';
import { MassUploadGridFormHelperService } from '../../../producer/mass-upload-grid/services/mug-form-helper.service';
import { DateService } from '../../../../shared/services/date.service';


@FormComponentLookup('mass-upload-grid-control')
@Component({
  selector: 'app-mass-upload-form-control',
  templateUrl: './mass-upload-form-control.component.html',
  styleUrl: './mass-upload-form-control.component.scss',
  standalone: false
})
export class MassUploadFormControlComponent extends FormEntryComponentClass{

  // This is needed to override the default EntryConfig type for this variable
  @Input() entry: MassUploadEntryConfig;
  
  @ViewChild('entryRef') entryRef: ElementRef;

  RADIO_COMPONENT = 'radio-input';
  CUSTOM_RADIO_COMPONENT = 'custom-radio-input';
  SELECT_COMPONENT = 'dropdown-select';
  CHECKBOX_COMPONENT = 'checkbox-input';
  CUSTOM_CHECKBOX_COMPONENT = 'custom-checkbox-input';
  DATE_INPUT = 'date-input';
  CUSTOM_DATE_INPUT = 'custom-date-input';
  DATE_OF_BIRTH = 'date-of-birth-input';

  dateComponentTypes = [
    this.DATE_INPUT,
    this.CUSTOM_DATE_INPUT,
    this.DATE_OF_BIRTH
  ];
  
  dropdownComponentTypes = [
    this.RADIO_COMPONENT, 
    this.CUSTOM_RADIO_COMPONENT, 
    this.SELECT_COMPONENT,
    this.CHECKBOX_COMPONENT,
    this.CUSTOM_CHECKBOX_COMPONENT
  ];

  isDropdown = false;
  dropdownOptions: DropdownOption[] = [];

  stateId: number;
  isLoading: boolean = false;

  constructor(
    protected mugService: MassUploadGridService,
    protected mugDataHelperService: MassUploadGridDataHelperService,
    protected mugFormHelperService: MassUploadGridFormHelperService,
    protected entitlementsHelperService: EntitlementsHelperService,
    protected dateService: DateService
  ) {
    super();
  }

  ngOnInit() {
    // State ID should always exist
    this.stateId = this.entry.massUploadAttributes.stateId;

    if (this.isDropdownComponent()) {
      this.isDropdown = true;
      this.dropdownOptions = this.getDropdownOptions();
    }
    else if (this.isDateComponent()) {
      console.error(`
        Cannot use generic form control with date field ${this.entry.formControlAttributes.formControlName}.
        Please update the json-excel mapping to use the custom component "mass-upload-grid-control-date" instead.
      `);
    }
  }

  // Almost the same as the parent function, but we omit the call to "setDefaultValue"
  initTriggers() {
    this.subscribeToFormStream(this.entry.base.formListeners);
    this.broadcastToFormStream(this.entry.base.formBroadcast);
    this.setFormControlValidators();
  }

  /**
   * @description Resets the form value specifically for the mass-upload-grid. 
   * The grid does not use the redux state, it relies on the formControl value and the .setDefaultValue property. 
   * NOTE: If value needs to be an object, you must override this method AND ensure 'setDefaultValue' is always a string.
   */
  resetFormValue(value?: string) {
    this.selfAsFormControl.setValue(value);
    this.entry.formControlAttributes.setDefaultValue = value;
  }

  // Helper method to get dropdown options
  getDropdownOptions(): Array<{ value: string|boolean; label: string }> {
    const emptyDropdownOption = [{label: '', value: null}];
    const componentType = this.entry.massUploadAttributes?.actualComponentType;
    
    if (componentType === this.RADIO_COMPONENT || componentType === this.CUSTOM_RADIO_COMPONENT) {
      return emptyDropdownOption.concat(this.entry.radioGroupAttributes?.optionAttributes);
    }
    else if (componentType === this.SELECT_COMPONENT) {
      return emptyDropdownOption.concat(this.entry.selectAttributes?.optionAttributes);
    }
    else if (componentType === this.CHECKBOX_COMPONENT || componentType === this.CUSTOM_CHECKBOX_COMPONENT) {
      return emptyDropdownOption.concat([{label: 'Yes', value: true}, {label: 'No', value: false}]);
    }
    
    return [];
  }

  // Helper method to check if component should render as dropdown
  isDropdownComponent(): boolean {
    const componentType = this.entry.massUploadAttributes?.actualComponentType;
    return this.dropdownComponentTypes.includes(componentType);
  }

  isDateComponent(): boolean {
    const componentType = this.entry.massUploadAttributes?.actualComponentType;
    return this.dateComponentTypes.includes(componentType);
  }

  onBlur() {
    this.entry.formControlAttributes.setDefaultValue = this.selfAsFormControl.value;
  }

  getZipCodeControl(): UntypedFormControl {
    return this.parentForm[this.mugDataHelperService.getSubscriberFormKey(this.stateId)].controls.zipCode;
  }

  getCountyControl(): UntypedFormControl {
    return this.parentForm[this.mugDataHelperService.getSubscriberFormKey(this.stateId)].controls.county;
  }

  getEffectiveDateControl(): UntypedFormControl {
    return this.parentForm[this.mugDataHelperService.getEffectiveDateFormKey(this.stateId)].controls.effectiveDate;
  }

  async getCounty(): Promise<County> {
    let countyControl = this.getCountyControl();
    if (!countyControl?.valid || !countyControl.value) {
      return null;
    }

    let countyName = countyControl.value;
    let countyResponse = await this.mugService.fetchIndividualCounty(this.mugDataHelperService.getZipCodeByStateId(this.stateId));

    if (countyResponse?.payload?.countyList?.length) {
      let county = countyResponse.payload.countyList.filter((county) => county.county === countyName?.toUpperCase())[0];
      return county;
    }

    return null;
  }
}
